import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { CONFIG } from "../../lib/config";
import { getEventTargetMs } from "../../lib/eventTime";

const DISMISS_KEY = "aktra_countdown_dismissed";

function fmt(ms) {
  const clamped = Math.max(0, ms);
  const h = Math.floor(clamped / (1000 * 60 * 60));
  const m = Math.floor((clamped / (1000 * 60)) % 60);
  const s = Math.floor((clamped / 1000) % 60);
  return { h, m, s };
}

export default function CountdownBanner() {
  const targetRef = useRef(getEventTargetMs());
  const [remaining, setRemaining] = useState(() => targetRef.current - Date.now());
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1"
  );

  useEffect(() => {
    let timeoutId;
    const tick = () => {
      setRemaining(targetRef.current - Date.now());
      const nextIn = 1000 - (Date.now() % 1000);
      timeoutId = setTimeout(tick, nextIn);
    };
    tick();
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        clearTimeout(timeoutId);
        tick();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const THRESHOLD = 48 * 60 * 60 * 1000;
  const visible = !dismissed && remaining > 0 && remaining <= THRESHOLD;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  const { h, m, s } = fmt(remaining);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="countdown-banner"
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-1/2 z-40 w-[calc(100%-1.5rem)] max-w-xl -translate-x-1/2 border border-[#FF0000]/70 bg-black/85 px-5 py-4 shadow-[0_0_40px_rgba(255,0,0,0.35)] backdrop-blur-xl"
        >
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="mb-1 flex items-center gap-2 font-mono-x text-[10px] uppercase tracking-[0.3em] text-[#FF0000]">
                <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#FF0000]" />
                Dernière ligne droite
              </div>
              <div className="font-display text-lg uppercase leading-none text-white sm:text-xl">
                Éclipse dans{" "}
                <span className="tabular-nums text-[#FF0000]">
                  {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s
                </span>
              </div>
            </div>
            <a
              href={CONFIG.weezeventUrl}
              data-testid="banner-buy"
              className="glow-red hidden shrink-0 bg-[#FF0000] px-5 py-3 font-mono-x text-[11px] font-semibold uppercase tracking-[0.15em] text-white hover:bg-[#ff2424] sm:inline-block"
            >
              Billet →
            </a>
            <button
              onClick={dismiss}
              data-testid="banner-close"
              className="shrink-0 border border-white/15 p-2 text-zinc-400 transition-colors hover:border-white/30 hover:text-white"
              aria-label="Fermer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
